import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  InputAdornment,
  IconButton,
  Chip,
  LinearProgress,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TradingIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Launch as LaunchIcon,
  Refresh as RefreshIcon,
  DeleteOutline as DisconnectIcon,
} from '@mui/icons-material';

interface BtraderAccount {
  fees: Record<string, any>;
  account_type: 'sandbox' | 'production';
  api_permissions: string[];
}

interface VerificationResult {
  success: boolean;
  message: string;
  account_info: BtraderAccount | null;
}

interface Balance {
  symbol: string;
  available: string;
  inOrder: string;
  total?: string;
}

interface BtraderApiSetupProps {
  onVerificationComplete?: (verified: boolean) => void;
  initialApiKey?: string;
  initialApiSecret?: string;
  isConnected?: boolean;
}

const BtraderApiSetup: React.FC<BtraderApiSetupProps> = ({
  onVerificationComplete,
  initialApiKey = '',
  initialApiSecret = '',
  isConnected = false,
}) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [apiKey, setApiKey] = useState(initialApiKey);
  const [apiSecret, setApiSecret] = useState(initialApiSecret);
  const [showSecret, setShowSecret] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null);
  const [accountInfo, setAccountInfo] = useState<BtraderAccount | null>(null);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);

  useEffect(() => {
    if (isConnected) {
      setActiveStep(2);
      (async () => {
        await fetchAccountInfo();
        await fetchBalances();
      })().catch(console.error);
    }
  }, [isConnected]);

  const steps = [
    {
      label: 'API-Keys eingeben',
      description: 'BTRADER API-Keys zur Verifikation eingeben',
    },
    {
      label: 'Verbindung testen',
      description: 'API-Keys verifizieren und Account-Informationen abrufen',
    },
    {
      label: 'Setup abgeschlossen',
      description: 'API erfolgreich verbunden und einsatzbereit',
    },
  ];

  const validateInputs = () => {
    if (!apiKey.trim()) {
      setError('API Key ist erforderlich');
      return false;
    }
    if (!apiSecret.trim()) {
      setError('API Secret ist erforderlich');
      return false;
    }
    if (apiKey.length < 20) {
      setError('API Key scheint ungültig zu sein (zu kurz)');
      return false;
    }
    if (apiSecret.length < 32) {
      setError('API Secret scheint ungültig zu sein (zu kurz)');
      return false;
    }
    setError(null);
    return true;
  };

  const handleVerifyConnection = async () => {
    if (!validateInputs()) return;

    setIsVerifying(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api/v1'}/btrader/verify-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('authTokens') || '{}').access_token}`,
        },
        body: JSON.stringify({
          api_key: apiKey.trim(),
          api_secret: apiSecret.trim(),
        }),
      });

      const result: VerificationResult = await response.json();
      setVerificationResult(result);

      if (result.success && result.account_info) {
        setAccountInfo(result.account_info);
        setActiveStep(2);
        await fetchBalances();
        onVerificationComplete?.(true);
      } else {
        setError(result.message);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Verbindungsfehler';
      setError(`Fehler bei der Verifikation: ${errorMessage}`);
      setVerificationResult({
        success: false,
        message: errorMessage,
        account_info: null,
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const fetchAccountInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api/v1'}/btrader/account-info`, {
        headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('authTokens') || '{}').access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAccountInfo(data.account_info);
      }
    } catch (err) {
      console.error('Failed to fetch account info:', err);
    }
  };

  const fetchBalances = async () => {
    setIsLoadingBalances(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api/v1'}/btrader/balance`, {
        headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('authTokens') || '{}').access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const formattedBalances = data.balances.map((balance: any) => ({
          symbol: balance.symbol,
          available: balance.available,
          inOrder: balance.inOrder,
          total: (parseFloat(balance.available) + parseFloat(balance.inOrder)).toFixed(8),
        }));
        setBalances(formattedBalances);
      }
    } catch (err) {
      console.error('Failed to fetch balances:', err);
    } finally {
      setIsLoadingBalances(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api/v1'}/btrader/disconnect`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('authTokens') || '{}').access_token}`,
        },
      });

      if (response.ok) {
        setActiveStep(0);
        setApiKey('');
        setApiSecret('');
        setAccountInfo(null);
        setBalances([]);
        setVerificationResult(null);
        setError(null);
        onVerificationComplete?.(false);
      }
    } catch (err) {
      setError('Fehler beim Trennen der Verbindung');
    }
  };

  const getStepIcon = (stepIndex: number) => {
    if (stepIndex < activeStep) return <CheckIcon />;
    if (stepIndex === activeStep && isVerifying) return <RefreshIcon className="animate-spin" />;
    return stepIndex + 1;
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <SecurityIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              BTRADER API Setup
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Verbinden Sie Ihr BTRADER-Konto für automatisierten Handel
            </Typography>
          </Box>
        </Box>

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                icon={getStepIcon(index)}
                sx={{
                  '& .MuiStepIcon-root': {
                    color: index <= activeStep ? theme.palette.primary.main : theme.palette.grey[400],
                  },
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {step.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </StepLabel>
              <StepContent>
                {index === 0 && (
                  <Box>
                    {/* Instructions */}
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        So erhalten Sie Ihre BTRADER API-Keys:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <LaunchIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>
                            <Link
                              href="https://account.btrader.com/api"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              BTRADER API-Einstellungen öffnen
                            </Link>
                          </ListItemText>
                        </ListItem>
                        <ListItem>
                          <ListItemText inset>
                            Neuen API-Key erstellen mit Berechtigungen: "Read" und "Trade"
                          </ListItemText>
                        </ListItem>
                        <ListItem>
                          <ListItemText inset>
                            API-Key und Secret hier eingeben
                          </ListItemText>
                        </ListItem>
                      </List>
                    </Alert>

                    {/* API Key Input */}
                    <TextField
                      fullWidth
                      label="BTRADER API Key"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Ihr BTRADER API Key"
                      margin="normal"
                      error={!!error && error.includes('API Key')}
                      InputProps={{
                        style: { fontFamily: 'monospace' },
                      }}
                    />

                    {/* API Secret Input */}
                    <TextField
                      fullWidth
                      label="BTRADER API Secret"
                      type={showSecret ? 'text' : 'password'}
                      value={apiSecret}
                      onChange={(e) => setApiSecret(e.target.value)}
                      placeholder="Ihr BTRADER API Secret"
                      margin="normal"
                      error={!!error && error.includes('API Secret')}
                      InputProps={{
                        style: { fontFamily: 'monospace' },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowSecret(!showSecret)}
                              edge="end"
                            >
                              {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {error && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                      </Alert>
                    )}

                    <Box mt={3}>
                      <Button
                        variant="contained"
                        onClick={handleVerifyConnection}
                        disabled={!apiKey.trim() || !apiSecret.trim() || isVerifying}
                        startIcon={isVerifying ? <RefreshIcon /> : <CheckIcon />}
                      >
                        {isVerifying ? 'Verifiziere...' : 'Verbindung testen'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {index === 1 && (
                  <Box>
                    {isVerifying && (
                      <Box mb={3}>
                        <LinearProgress />
                        <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
                          API-Verbindung wird getestet...
                        </Typography>
                      </Box>
                    )}

                    {verificationResult && (
                      <Alert
                        severity={verificationResult.success ? "success" : "error"}
                        sx={{ mb: 2 }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {verificationResult.success ? 'Verbindung erfolgreich!' : 'Verbindung fehlgeschlagen'}
                        </Typography>
                        <Typography variant="body2">
                          {verificationResult.message}
                        </Typography>
                      </Alert>
                    )}

                    {verificationResult?.success && accountInfo && (
                      <Box>
                        <Typography variant="h6" fontWeight={600} mb={2}>
                          Account-Informationen
                        </Typography>
                        <Box display="flex" gap={1} mb={2}>
                          <Chip
                            label={accountInfo.account_type === 'sandbox' ? 'Testnet' : 'Live Trading'}
                            color={accountInfo.account_type === 'sandbox' ? 'warning' : 'success'}
                            size="small"
                          />
                          {(accountInfo.api_permissions ?? []).map(permission => (
                            <Chip key={permission} label={permission} variant="outlined" size="small" />
                          ))}
                        </Box>
                        <Button
                          variant="contained"
                          onClick={() => setActiveStep(2)}
                          startIcon={<CheckIcon />}
                        >
                          Setup abschließen
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {index === 2 && (
                  <Box>
                    <Alert severity="success" sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        BTRADER API erfolgreich verbunden!
                      </Typography>
                      <Typography variant="body2">
                        Sie können jetzt Trading-Orders über das System platzieren.
                      </Typography>
                    </Alert>

                    {/* Account Status */}
                    <Box mb={3}>
                      <Typography variant="h6" fontWeight={600} mb={2}>
                        Account-Status
                      </Typography>
                      <Box display="flex" gap={2} mb={2}>
                        <Chip
                          icon={<AccountBalanceIcon />}
                          label={accountInfo?.account_type === 'sandbox' ? 'Testnet-Modus' : 'Live-Trading'}
                          color={accountInfo?.account_type === 'sandbox' ? 'warning' : 'success'}
                        />
                        <Chip
                          icon={<TradingIcon />}
                          label="Trading aktiviert"
                          color="success"
                        />
                      </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Balances */}
                    <Box mb={3}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" fontWeight={600}>
                          Kontoguthaben
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={fetchBalances}
                          disabled={isLoadingBalances}
                        >
                          Aktualisieren
                        </Button>
                      </Box>

                      {isLoadingBalances ? (
                        <LinearProgress />
                      ) : balances.length > 0 ? (
                        <Box>
                          {balances.map((balance) => (
                            <Box
                              key={balance.symbol}
                              p={2}
                              mb={1}
                              borderRadius={1}
                              sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {balance.symbol}
                                </Typography>
                                <Typography variant="body2" fontFamily="monospace">
                                  {balance.total} {balance.symbol}
                                </Typography>
                              </Box>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                  Verfügbar: {balance.available} | In Order: {balance.inOrder}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Keine Guthaben mit positivem Saldo gefunden
                        </Typography>
                      )}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Actions */}
                    <Box display="flex" gap={2}>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DisconnectIcon />}
                        onClick={() => setConfirmDisconnectOpen(true)}
                      >
                        API-Verbindung trennen
                      </Button>
                      <Dialog open={confirmDisconnectOpen} onClose={() => setConfirmDisconnectOpen(false)}>
                        <DialogTitle>Bist du sicher?</DialogTitle>
                        <DialogContent>
                          <DialogContentText>
                            Möchtest du die API-Verbindung wirklich trennen? Du kannst danach nicht mehr traden.
                          </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setConfirmDisconnectOpen(false)}>Abbrechen</Button>
                          <Button onClick={() => { setConfirmDisconnectOpen(false); handleDisconnect(); }} color="error" variant="contained">
                            Ja, trennen
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </Box>
                  </Box>
                )}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </CardContent>
    </Card>
  );
};

export default BtraderApiSetup;